import React, {Component} from 'react'
import PropTypes from 'prop-types'
import styles from "./DatePicker.module.scss";
import moment from "moment";
import Years from "./Years/Years";
import Months from "./Months/Months";
import Calendar from "./Calendar/Calendar";
import Navigator from "./Controls/Navigator";
import {debounce} from "lodash";
import Swipe from 'react-easy-swipe';

class DatePicker extends Component {

    static EDIT_OPTIONS = {
        DAYS: 'days',
        MONTHS: 'months',
        YEARS: 'years',
    };

    constructor(props) {
        super(props);

        this.state = {
            pivot: props.start ? props.start.clone() : props.pivot.clone(),
            keyAnchor: props.start ? props.start.clone() : props.pivot.clone(),
            start: props.start ? props.start.clone() : null,
            end: props.end ? props.end.clone() : null,
            selectingRange: null,
            selecting: false,
            calendarInlineStyle: {},
            canSwipe: true,
            editType: props.isYearPicker
                ? DatePicker.EDIT_OPTIONS.YEARS
                : props.isMonthPicker
                    ? DatePicker.EDIT_OPTIONS.MONTHS
                    : DatePicker.EDIT_OPTIONS.DAYS
        }
    }

    componentDidMount() {
        this.mounted = true;
        const {acceptKeyboardControls} = this.props;
        if (acceptKeyboardControls) {
            window.addEventListener('keydown', debounce(this.keyUpHandler, 25));
        }
    };

    /*componentDidUpdate(prevProps, prevState, snapshot) {
        const {acceptKeyboardControls} = this.props;
        if (acceptKeyboardControls) {
            window.addEventListener('keydown', this.keyDownPaxTypeHandler);
        }
    }*/

    componentWillUnmount() {
        window.removeEventListener('keydown', this.keyUpHandler);
        this.mounted = false;
    }

    keyUpHandler = e => {
        if (this.mounted) {
            this.actionCreator(e);
        }
    };

    actionCreator = e => {
        const {calendars} = this.props;
        const {keyAnchor, pivot} = this.state;
        const updatedAnchor = keyAnchor.clone();
        const calendarsFirstMonth = pivot.clone();
        const calendarsLastMonth = pivot.clone().add(calendars-1, 'month');

        const code = e.code ? e.code : e.key;

        switch (code) {
            case 'Up':
            case 'ArrowUp':
                updatedAnchor.subtract(1, 'week');
                if (updatedAnchor.isBefore(calendarsFirstMonth, 'month')) {
                    if (!this.canNavigateBack()) break;
                    this.navigatePrev();
                }
                this.setState({keyAnchor: updatedAnchor.clone()},() => this.dayHoveringHandler(updatedAnchor.clone()));
                break;
            case 'Down':
            case 'ArrowDown':
                updatedAnchor.add(1, 'week');
                if (updatedAnchor.isAfter(calendarsLastMonth, 'month')) {
                    if (!this.canNavigateNext()) break;
                    this.navigateNext();
                }
                this.setState({keyAnchor: updatedAnchor.clone()},() => this.dayHoveringHandler(updatedAnchor.clone()));
                break;
            case 'Right':
            case 'ArrowRight':
                updatedAnchor.add(1, 'day');
                if (updatedAnchor.isAfter(calendarsLastMonth, 'month')) {
                    if (!this.canNavigateNext()) break;
                    this.navigateNext();
                }
                this.setState({keyAnchor: updatedAnchor.clone()}, () => this.dayHoveringHandler(updatedAnchor.clone()));
                break;
            case 'Left':
            case 'ArrowLeft':
                updatedAnchor.subtract(1, 'day');
                if (updatedAnchor.isBefore(calendarsFirstMonth, 'month')) {
                    if (!this.canNavigateBack()) break;
                    this.navigatePrev();
                }
                this.setState({keyAnchor: updatedAnchor.clone()},() => this.dayHoveringHandler(updatedAnchor.clone()));
                break;
            case ' ':
            case 'Space':
            case 'NumpadEnter':
            case 'Enter':
                if (!this.isDisabledDate(keyAnchor.clone())) {
                    this.select(keyAnchor.clone());
                }
                break;
            default:
                return;
        }

        e.stopPropagation();
        e.preventDefault();
    };

    isDisabledDate = date => {
        const {disabled, pastDisabled, todayDisabled, filterDaysToDisableHandler, availableRange} = this.props;

        const today = moment();
        const unit = 'day';
        return (disabled ||
            (pastDisabled && date.isBefore(today, unit)) ||
            (todayDisabled && date.isSame(today, unit)) ||
            filterDaysToDisableHandler(date) ||
            (availableRange && (date.isBefore(availableRange.start, unit) || date.isAfter(availableRange.end, unit)))
        );
    };

    navigateNext = () => {
        const {pivot} = this.state;
        const {n, unit} = this.getEditTypeNavigationUnits();
        pivot.add(n, unit);
        this.setState({pivot});
    };

    navigatePrev = () => {
        const {pivot} = this.state;
        const {n, unit} = this.getEditTypeNavigationUnits();
        pivot.subtract(n, unit);
        this.setState({pivot});
    };

    isValidEditType = editType => {
        return Object.values(DatePicker.EDIT_OPTIONS).indexOf(editType) !== -1;
    };

    isEditTypeAllowed = editType => {
        if (!this.isValidEditType(editType)) {
            return false;
        }

        const {abstractToMonths, abstractToDecades, isMonthPicker, isYearPicker, navigable} = this.props;
        const cannotGoToYears = !abstractToDecades || isYearPicker;
        const cannotGoToMonths = !abstractToMonths || isYearPicker;
        const cannotGoToDays = isYearPicker || isMonthPicker;
        return !((editType === DatePicker.EDIT_OPTIONS.YEARS && cannotGoToYears) ||
            (editType === DatePicker.EDIT_OPTIONS.MONTHS && cannotGoToMonths) ||
            (editType === DatePicker.EDIT_OPTIONS.DAYS && cannotGoToDays)) && navigable;
    };

    switchEditType = editType => {
        if (!this.isEditTypeAllowed(editType)) {
            return;
        }

        this.setState({editType});
    };

    getEditTypeNavigationUnits = () => {
        const {editType} = this.state;

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.DAYS:
                return {n: 1, unit: 'month'};
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return {n: 1, unit: 'year'};
            case DatePicker.EDIT_OPTIONS.YEARS:
                return {n: 10, unit: 'year'};
            default:
                return {n: 1, unit: 'month'};
        }
    };

    canNavigateBack = () => {
        const {editType, pivot, disabled} = this.state;
        const {pastDisabled, availableRange, navigable} = this.props;
        const today = moment();

        if (disabled || !navigable) {
            return false;
        }

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.DAYS:
                return (!pastDisabled || (pastDisabled && pivot.isAfter(today, 'month')))
                    && (!availableRange || (availableRange && pivot.isAfter(availableRange.start, 'month')));
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return (!pastDisabled || (pastDisabled && pivot.isAfter(today, 'year')))
                    && (!availableRange || (availableRange && pivot.isAfter(availableRange.start, 'year')));
            case DatePicker.EDIT_OPTIONS.YEARS:
                return (!pastDisabled || (pastDisabled && pivot.isAfter(today, 'year')))
                    && (!availableRange || (availableRange && pivot.isAfter(availableRange.start, 'year')));//todo 10y
            default:
                return true;
        }
    };

    canNavigateNext = () => {
        const {editType, pivot, disabled} = this.state;
        const {availableRange, navigable} = this.props;

        if (disabled || !navigable) {
            return false;
        }

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.DAYS:
                return availableRange ? pivot.isBefore(availableRange.end, 'month') : true;
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return availableRange ? pivot.isBefore(availableRange.end, 'year') : true;
            case DatePicker.EDIT_OPTIONS.YEARS:
                return availableRange ? pivot.isBefore(availableRange.end, 'year') : true;//todo 10y
            default:
                return true;
        }
    };

    canAbstract = () => {
        const {editType} = this.state;

        const {abstractToMonths, abstractToDecades, navigable} = this.props;

        return ((abstractToMonths && editType === DatePicker.EDIT_OPTIONS.DAYS)
            || (abstractToDecades && editType === DatePicker.EDIT_OPTIONS.MONTHS)
            || editType === DatePicker.EDIT_OPTIONS.YEARS) && navigable;
    };

    abstractEditType = () => {
        const {editType} = this.state;

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.DAYS:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.MONTHS);
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.YEARS);
            default:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.DAYS);
        }
    };

    focusEditType = () => {
        const {editType} = this.state;

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.YEARS:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.MONTHS);
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.DAYS);
            default:
                return this.switchEditType(DatePicker.EDIT_OPTIONS.DAYS);
        }
    };

    updatePivotAndFocus = pivot => {
        this.setState({pivot}, () => this.focusEditType());
    };

    select = date => {
        const {selectedDateHandler, selectedRangeHandler, isRangePicker} = this.props;
        const {selecting, start} = this.state;

        if (!isRangePicker) {
            return this.setState(
                {start: date.clone(), end: date.clone(), selecting: false, selectingRange: null},
                () => selectedDateHandler({date: date.clone(), start: date.clone(), end: date.clone()})
            );
        }

        if (!selecting) {
            return this.setState({
                start: date.clone(),
                end: date.clone(),
                selecting: true,
                selectingRange: {
                    start: date.clone(),
                    end: date.clone()
                }
            });
        }

        if (start instanceof moment && date instanceof moment && date.isBefore(start, 'day')) {
            return this.setState(
                {start: date.clone(), end: start.clone(), selecting: false, selectingRange: null},
                () => selectedRangeHandler({start: date.clone(), end: start.clone()})
            );
        }

        return this.setState(
            {end: date.clone(), selecting: false, selectingRange: null},
            () => selectedRangeHandler({start: start.clone(), end: date.clone()})
        );
    };

    dayHoveringHandler = dayHovered => {
        const {selecting, start} = this.state;
        if (start instanceof moment && selecting) {
            if (dayHovered.isBefore(start, 'days')) {
                return this.setState({selectingRange: {start: dayHovered.clone(), end: start.clone()}});
            }

            return this.setState({selectingRange: {start: start.clone(), end: dayHovered.clone()}});
        }
    };

    renderContent = () => {
        const {editType, pivot, start, end, keyAnchor, selectingRange, calendarInlineStyle} = this.state;
        const {
            pastDisabled, todayDisabled, disabled, availableRange, isYearPicker, overrideDayRender, monthOverflowDaysVisible,
            isMonthPicker, calendars, filterDaysToDisableHandler, acceptKeyboardControls, mobile, t
        } = this.props;
        const contentClasses = [styles.content];
        if (mobile) contentClasses.push(styles.mobile);

        let jsx = null;

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.YEARS:
                jsx = (
                    <Years pivot={pivot}
                           selectYearHandler={isYearPicker ? this.select : this.updatePivotAndFocus}
                           start={start}
                           end={end}
                           disabled={disabled}
                           pastDisabled={pastDisabled}
                           availableRange={availableRange}
                           inlineStyle={calendarInlineStyle}
                           t={t}
                    />
                );
                break;
            case DatePicker.EDIT_OPTIONS.MONTHS:
                jsx = (
                    <Months pivot={pivot}
                            selectMonthHandler={isMonthPicker ? this.select : this.updatePivotAndFocus}
                            start={start}
                            end={end}
                            disabled={disabled}
                            pastDisabled={pastDisabled}
                            availableRange={availableRange}
                            inlineStyle={calendarInlineStyle}
                            t={t}
                    />
                );
                break;
            default:
                jsx = [];
                for (let i = 0; i < calendars; i++) {
                    jsx.push(
                        <Calendar key={`calendar-${i}`}
                                  pivot={pivot.clone().add(i, 'month')}
                                  keyAnchor={acceptKeyboardControls ? keyAnchor : null}
                                  selectDayHandler={this.select}
                                  hoveredDayHandler={this.dayHoveringHandler}
                                  filterDaysToDisableHandler={filterDaysToDisableHandler}
                                  monthOverflowDaysVisible={monthOverflowDaysVisible}
                                  start={start}
                                  end={end}
                                  disabled={disabled}
                                  pastDisabled={pastDisabled}
                                  todayDisabled={todayDisabled}
                                  availableRange={availableRange}
                                  selectingRange={selectingRange}
                                  overrideDayRender={overrideDayRender}
                                  inlineStyle={calendarInlineStyle}
                                  t={t}
                        />
                    );
                }
                break;
        }

        return (
            <div className={contentClasses.join(' ')}>
                {jsx}
            </div>
        );
    };

    renderCurrentLabel = () => {
        const {editType, pivot} = this.state;
        const {calendars, t} = this.props;
        const year = pivot.format('YYYY');
        const decadeStart = year - (year % 10);
        const decadeEnd = year - (year % 10) + 10;
        const decade = decadeStart + ' - ' + decadeEnd;
        const month = t(pivot.format('MMMM'));
        const lastMonthOnCalendars = pivot.clone().add(calendars - 1, 'month');

        switch (editType) {
            case DatePicker.EDIT_OPTIONS.DAYS:
                const months = [];
                for (let m = pivot.clone(); m.isSameOrBefore(lastMonthOnCalendars.clone()); m.add(1, 'month')) {
                    months.push(<div key={'month-'+m.format('YYYY-MM')}>{t(m.format('MMMM')) + ' ' + m.format('YYYY')}</div>);
                }
                return months;
            case DatePicker.EDIT_OPTIONS.MONTHS:
                return year;
            case DatePicker.EDIT_OPTIONS.YEARS:
                return decade;
            default:
                return month + ' ' + year;
        }
    };

    onSwipeMove = (position, e) => {
        this.setState({
            calendarInlineStyle: {
                transform: `translateX(${position.x}px)`
            }
        });

        return true;
    };

    onSwipeLeft = () => {
        if (this.canNavigateNext()) {
            this.navigateNext();
        }

        this.setState({
            calendarInlineStyle: {
                transform: 'translateX(0)'
            }
        });
    };

    onSwipeRight = () => {
        if (this.canNavigateBack()) {
            this.navigatePrev();
        }

        this.setState({
            calendarInlineStyle: {
                transform: 'translateX(0)'
            }
        });
    };

    renderControls = () => {
        const {noNavigation} = this.props;
        if (noNavigation) {
            return null;
        }

        const labelClasses = [styles.label];
        if (!this.canAbstract()) {
            labelClasses.push(styles.labelDisabled);
        }

        return (
            <div className={styles.controls}>
                <Navigator clickHandler={this.navigatePrev} disabled={!this.canNavigateBack()}>
                    &#60;
                </Navigator>
                <div className={labelClasses.join(' ')} onClick={this.abstractEditType}>
                    {this.renderCurrentLabel()}
                </div>
                <Navigator clickHandler={this.navigateNext} disabled={!this.canNavigateNext()}>
                    &#62;
                </Navigator>
            </div>
        );

    };

    render() {
        const {children, isOpen} = this.props;

        if (!isOpen) {
            return null;
        }

        return (
            <div className={styles.container}>
                {this.renderControls()}
                <Swipe onSwipeMove={this.onSwipeMove}
                       onSwipeLeft={this.onSwipeLeft}
                       onSwipeRight={this.onSwipeRight}
                       allowMouseEvents={false}>
                    {this.renderContent()}
                </Swipe>
                {children}
            </div>
        );
    }
}

DatePicker.propTypes = {
    children: PropTypes.any,
    isOpen: PropTypes.bool,
    monthOverflowDaysVisible: PropTypes.bool,
    acceptKeyboardControls: PropTypes.bool,
    isRangePicker: PropTypes.bool,
    isMonthPicker: PropTypes.bool,
    isYearPicker: PropTypes.bool,
    abstractToMonths: PropTypes.bool,
    abstractToDecades: PropTypes.bool,
    pivot: PropTypes.instanceOf(moment),
    start: PropTypes.instanceOf(moment),
    end: PropTypes.instanceOf(moment),
    selectedDateHandler: PropTypes.func,
    selectedRangeHandler: PropTypes.func,
    calendars: PropTypes.oneOf([1, 2, 3, 4]),
    navigable: PropTypes.bool,
    noNavigation: PropTypes.bool,
    mobile: PropTypes.bool,
    disabled: PropTypes.bool,
    pastDisabled: PropTypes.bool,
    todayDisabled: PropTypes.bool,
    filterDaysToDisableHandler: PropTypes.func,
    availableRange: PropTypes.shape({
        start: PropTypes.instanceOf(moment),
        end: PropTypes.instanceOf(moment)
    }),
    overrideDayRender: PropTypes.func,
    t: PropTypes.func,
};

DatePicker.defaultProps = {
    children: null,
    isOpen: true,
    monthOverflowDaysVisible: true,
    acceptKeyboardControls: false,
    isRangePicker: false,
    isMonthPicker: false,
    isYearPicker: false,
    abstractToMonths: true,
    abstractToDecades: true,
    pivot: moment(),
    start: null,
    end: null,
    selectedDateHandler: () => null,
    selectedRangeHandler: () => null,
    calendars: 1,
    navigable: true,
    noNavigation: false,
    mobile: false,
    disabled: false,
    pastDisabled: false,
    todayDisabled: false,
    filterDaysToDisableHandler: () => false,
    availableRange: null,
    overrideDayRender: null,
    t: str => str
};

export default DatePicker;